import { Injectable, inject } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { SalesConsultantListingFormRequest, SalesConsultantListingFormResponse } from "../../../features/sales/models/sales-consultant-listing-form-request.model";
import { SalesTermsApprovalRequest, SalesTermsFormRequest, SalesTermsFormResponse } from "../../../features/sales/models/sales-terms-form-request.model";
import { SalesForm } from "../../../features/sales/models/sales-agent.model";
import { PresignedResponse } from "../../admin/models/PresignedResponse";
import { SalesW9FormRequest, SalesW9FormResponse } from "../../../features/sales/models/sales-w9-form-request.model";

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
    private http = inject(HttpClient);

    private apiUrl = environment.apiUrl;
    private formState = new BehaviorSubject<any>(null);
    private formValidSource = new BehaviorSubject<boolean>(false);
    formValid$ = this.formValidSource.asObservable();

    setFormValidity(isValid: boolean) {
        this.formValidSource.next(isValid);
    }
    
    saveCurrentForm(data: any) {
        this.formState.next(data);
        localStorage.setItem('pending_signature', JSON.stringify(data));
    }

    loadSavedForm(): Observable<any> {
        const saved = localStorage.getItem('pending_signature');
        // Wrap the result in 'of()' to make it pipeable
        return of(saved ? JSON.parse(saved) : null);
    }

    clearFormState() {
        this.formState.next(null);
        localStorage.removeItem('pending_signature');
    }

    getListingTemplate() {
        return this.http.get(`${this.apiUrl}/document/template/listing`, { responseType: 'arraybuffer' });
    }

    getTermsTemplate() {
        return this.http.get(`${this.apiUrl}/document/template/terms`, { responseType: 'arraybuffer' });
    }

    getW9Template() {
        return this.http.get(`${this.apiUrl}/document/template/w9`, { responseType: 'arraybuffer' });
    }
    
    previewListing(request: SalesConsultantListingFormRequest): Observable<any> {
        return this.http.post(`${this.apiUrl}/document/preview/listing`, request, { 
            responseType: 'arraybuffer' // Get the raw bytes immediately
        });
    }

    previewTerms(request: SalesTermsFormRequest): Observable<any> {
        return this.http.post(`${this.apiUrl}/document/preview/terms`, request, {
            responseType: 'arraybuffer'
        });
    }

    previewW9(request: SalesW9FormRequest): Observable<any> {
        return this.http.post(`${this.apiUrl}/document/preview/w9`, request, {
            responseType: 'arraybuffer'
        });
    }

    finalizeListing(request: SalesConsultantListingFormRequest): Observable<SalesConsultantListingFormResponse> {
        return this.http.post<SalesConsultantListingFormResponse>(`${this.apiUrl}/document/sign/listing`, request);
    }

    finalizeTerms(request: SalesTermsFormRequest): Observable<SalesTermsFormResponse> {
        return this.http.post<SalesTermsFormResponse>(`${this.apiUrl}/document/sign/terms`, request);
    }

    finalizeW9(request: SalesW9FormRequest): Observable<SalesW9FormResponse> {
        return this.http.post<SalesW9FormResponse>(`${this.apiUrl}/document/sign/w9`, request);
    }

    approveTermsForm(request: SalesTermsApprovalRequest) {
        return this.http.post(`${this.apiUrl}/document/approve/terms`, request)
    }

    approveW9Form(request: SalesTermsApprovalRequest) {
        return this.http.post(`${this.apiUrl}/document/approve/w9`, request)
    }

    signListingDocument(request: SalesConsultantListingFormRequest): Observable<SalesConsultantListingFormResponse> {
        return this.http.post<SalesConsultantListingFormResponse>(`${this.apiUrl}/document/sign-listing`, request);
    }

    reviseListingDocument(request: SalesConsultantListingFormRequest): Observable<SalesConsultantListingFormResponse> {
        return this.http.post<SalesConsultantListingFormResponse>(`${this.apiUrl}/document/sign-listing/revise`, request);
    }

    submitListingDocument(request: SalesConsultantListingFormRequest): Observable<SalesConsultantListingFormResponse> {
        return this.http.post<SalesConsultantListingFormResponse>(`${this.apiUrl}/document/sign-listing/submit`, request);
    }

    signTermsDocument(request: SalesTermsFormRequest): Observable<SalesTermsFormResponse> {
        return this.http.post<SalesTermsFormResponse>(`${this.apiUrl}/document/sign-terms`, request);
    }

    signW9Document(request: SalesW9FormRequest): Observable<SalesW9FormResponse> {
        return this.http.post<SalesW9FormResponse>(`${this.apiUrl}/document/sign-w9`, request);
    }

    getListingForms() {
        return this.http.get<SalesForm[]>(`${this.apiUrl}/document/listing-forms`);
    }
    
    getTermsForms() {
        return this.http.get<SalesForm[]>(`${this.apiUrl}/document/terms-forms`);
    }

    getW9Forms() {
        return this.http.get<SalesForm[]>(`${this.apiUrl}/document/w9-forms`);
    }

    getPresignedUrl(filename: string): Observable<PresignedResponse> {
        console.log('filename', filename);
        return this.http.post<PresignedResponse>(`${this.apiUrl}/document/get-link`, { filename: filename });
    }
}